import {
  Box,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import { Close as CloseIcon } from '@mui/icons-material';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomButton from '../../../../../components/General/CustomButton';
import CustomModal from '../../../../../components/General/CustomModal';
import { ReduxState } from '../../../../../types';
import useStyles from '../../styles';
import AddDevicesFlowSecondStep from './AddDevicesFlowSecondStep';
import AddDevicesFlowThirdStep from './AddDevicesFlowThirdStep';
// import apiClient from '../../../../../requests/api/apiClient';
import axios from '../../../../../utils/axios';
import {
  DeviceInfo,
  ErrorState,
} from './types';
import useNodes from '../../../../../hooks/useNodes';
import { setAllFarmDevices } from '../../../../../actions/farmSectorActions';

const initialErrorState = {
  name: '',
  deviceType: '',
  farm: '',
  wiseconnId: '',
  sensorType: '',
  sensorId: '',
  nodeId: '',
  nodeType: '',
  mac: '',
  zoneId: '',
  softlandModel: '',
  softlandBrand: '',
};

const initialDeviceInfoState = {
  // firstStepInfo
  name: '',
  deviceType: '',
  farm: '',
  sectorId: '',
  wiseconnId: '',
  subSector: '',
  dispatchGuideId: '',
  // SecondStepPossibleInfo
  // -- Common
  comment: '',
  brand: '',
  model: '',
  nodeId: '', // for node and for sensor association with node
  // -- sensor common
  sensorId: '',
  // -- sensor & !dispatchGuide
  sensorType: '',
  // -- sensor & dispatchGuide
  // -- node common
  isGatewayNode: false,
  // -- node & !dispatchGuide
  nodeType: '',
  // -- weather station specific
  isWeatherStation: false,
  zoneId: '',
  // thirdStep
  coordinates: null,
  telemetryType: 'wiseconn',
  softlandModel: '',
  softlandBrand: '',
};

const AddDevicesFlow = ({
  isModalOpen,
  setModalOpen,
}: {
  isModalOpen: boolean,
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);
  const { nodes } = useNodes(selectedFarm?.id ?? '');
  const [steps, setSteps] = useState({
    secondStep: false,
    thirdStep: false,
  });
  const dispatch = useDispatch();
  const { allFarmDevices } = useSelector((state: ReduxState) => state.farmSector);

  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>(initialDeviceInfoState);
  const [error, setError] = useState<ErrorState>(initialErrorState);

  const [coordinates, setCoordinates] = useState<{ lng: number, lat: number }[] | null>(null);
  const [files, setFiles] = useState<Array<{ id: string, file: File }>>([]);

  useEffect(() => {
    if (selectedFarm?.name) {
      setDeviceInfo((prevInfo) => ({
        ...prevInfo,
        farm: selectedFarm.name,
      }));
    }
  }, [selectedFarm, isModalOpen]);

  const handleValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    source: string,
  ) => {
    const { value } = e.target;
    setDeviceInfo((prevState) => ({ ...prevState, [source]: value }));
    if (source === 'deviceType' || source === 'farm') {
      setError((prevState) => ({
        ...prevState,
        [source]: '',
      }));
    }
  };

  const handleStep = (source: 'firstStep' | 'secondStep' | 'thirdStep') => {
    if (source === 'firstStep') {
      setModalOpen(true);
      setSteps({
        secondStep: false,
        thirdStep: false,
      });
    }

    if (source === 'secondStep') {
      if ((!deviceInfo.farm || deviceInfo.farm === '')) {
        setError((prevState) => ({
          ...prevState,
          farm: 'Debes seleccionar un campo para avanzar',
        }));
        console.log('farm error return');
        return;
      }

      if (deviceInfo.deviceType === 'sensor' && nodes.length === 0) {
        enqueueSnackbar('Primero debes agregar un nodo para poder asociar un sensor', { variant: 'error' });
        return;
      }
      setModalOpen(false);
      setSteps({
        secondStep: true,
        thirdStep: false,
      });
    }

    if (source === 'thirdStep') {
      // isSensor
      if (deviceInfo.deviceType === 'sensor') {
        if (deviceInfo.name === '') {
          setError((prevState) => ({
            ...prevState,
            name: 'Debes proporcionar un nombre para avanzar',
          }));
          return;
        }
        if (deviceInfo.sensorType === '') {
          setError((prevState) => ({
            ...prevState,
            sensorId: 'Debes seleccionar un tipo de sensor para avanzar',
          }));
          return;
        }
        if (deviceInfo.sectorId === '' && deviceInfo.zoneId === '') {
          setError((prevState) => ({
            ...prevState,
            name: 'Si no seleccionaste un sector en el paso anterior, debes proporcionar un Zone ID',
          }));
          return;
        }
        if (
          deviceInfo.sensorType !== 'clima'
          && deviceInfo.telemetryType === 'wiseconn'
          && deviceInfo.wiseconnId === ''
        ) {
          setError((prevState) => ({
            ...prevState,
            wiseconnId: 'Debes proporcionar un Wiseconn ID para avanzar'
              + ' si la telemetría es Wiseconn',
          }));
          return;
        }
        if (deviceInfo.nodeId === '') {
          setError((prevState) => ({
            ...prevState,
            nodeId: 'Debes seleccionar un nodo para avanzar',
          }));
          return;
        }
      } else {
      // is Node
        if (deviceInfo.name === '') {
          setError((prevState) => ({
            ...prevState,
            name: 'Debes proporcionar un nombre para avanzar',
          }));
          return;
        }
        if (deviceInfo.nodeType === '') {
          setError((prevState) => ({
            ...prevState,
            nodeType: 'Debes seleccionar un tipo de nodo para avanzar',
          }));
          return;
        }
        if (deviceInfo.wiseconnId === '' && deviceInfo.telemetryType === 'wiseconn') {
          setError((prevState) => ({
            ...prevState,
            wiseconnId: 'Debes proporcionar un Wiseconn ID para avanzar si la telemetría es Wiseconn',
          }));
          return;
        }
      }

      setSteps({
        secondStep: false,
        thirdStep: true,
      });
    }
  };

  const handleCloseAndResetData = () => {
    setError(initialErrorState);
    setDeviceInfo(initialDeviceInfoState);
    setSteps({
      secondStep: false,
      thirdStep: false,
    });
    setCoordinates(null);
    setModalOpen(false);
    setFiles([]);
    window.location.reload();
  };

  const insertFunction = async () => {
  // eslint-disable-next-line prefer-const
    const dataToSend: { [key: string]: number | string | boolean } = {};
    Object.entries(deviceInfo).forEach(([key, value]) => {
      if (key !== 'farm' && value !== '') {
        dataToSend[key] = value;
      }
    });
    if (deviceInfo.telemetryType) {
      dataToSend.telemetryType = deviceInfo.telemetryType;
    }

    if (deviceInfo.isWeatherStation) {
      dataToSend.isWeatherStation = deviceInfo.isWeatherStation;
      dataToSend.zoneId = deviceInfo.zoneId ?? '';
    }

    try {
      const formData = new FormData();
      files.forEach((singleFile) => {
        formData.append('files', singleFile.file);
      });
      formData.append('data', JSON.stringify({
        ...dataToSend,
        farmId: selectedFarm?.id ?? '',
        coordinates: coordinates ? coordinates[0] : null,
      }));

      // apiClient not was working correclty on creating multiple images
      if (deviceInfo.deviceType === 'node') {
        const { data: nodeData } = await axios.post('/api/nodes', formData, {
          headers: {
            'content-type': 'multipart/form-data',
          },
        });
        dispatch(setAllFarmDevices([...allFarmDevices, nodeData]));
        enqueueSnackbar(nodeData.message, { variant: 'success' });
      } else {
        const { data: sensorData } = await axios.post('/api/sensors', formData, {
          headers: {
            'content-type': 'multipart/form-data',
          },
        });
        dispatch(setAllFarmDevices([...allFarmDevices, sensorData]));
        enqueueSnackbar(sensorData.message, { variant: 'success' });
      }
      handleCloseAndResetData();
    } catch (err) {
      const { response: fetchError } = err as AxiosError;
      if (fetchError?.data) {
        enqueueSnackbar((fetchError.data as { message: string }).message, { variant: 'error' });
      } else {
        const auxError = err as Error;
        enqueueSnackbar(auxError.message, { variant: 'error' });
      }
    }
  };

  return (
  <CustomModal
    open={isModalOpen || steps.secondStep || steps.thirdStep}
    handleClose={handleCloseAndResetData}
    cardClassName={steps.thirdStep ? classes.cardModalContainer : undefined}
  >
    <>
      {isModalOpen
        && <Box display='flex' flexDirection='column' flex='1'>
          <Box className={classes.uploadAddDevicesHeader}>
            <Typography
              variant="h3"
              color="textPrimary"
              className={classes.uploadAddDevicesHeaderTitle}
            >
              <span className={classes.activePageUnderlined}>
                Agregar equipo
              </span>
            </Typography>
            <IconButton onClick={handleCloseAndResetData}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider className={classes.divider} />
          <Box className={classes.textFieldsContainer}>
            <TextField
              label='Tipo (*)'
              variant='standard'
              select
              fullWidth
              error={error.deviceType !== ''}
              helperText={error.deviceType !== '' && error.deviceType}
              className={classes.singleLeftTextfield}
              value={deviceInfo.deviceType}
              onChange={(e) => handleValueChange(e, 'deviceType')}
            >
              <MenuItem value={'node'}>
                Nodo
              </MenuItem>
              <MenuItem value={'sensor'}>
                Sensor
              </MenuItem>
            </TextField>
            <TextField
              label='Campo'
              variant='standard'
              // select
              fullWidth
              error={error.farm !== ''}
              helperText={error.farm !== '' && error.farm}
              value={deviceInfo.farm}
              disabled
            // onChange={(e) => handleValueChange(e, 'farm')}
            />
            {/* CAMBIAR DESPOUES - REVISAR. Se agrega solo al que ya elegimos,
                o podemos agregar a cualquiera */}
            {/* {farmsOptions
                  ? farmsOptions.map((farm) => (
                    <MenuItem key={farm.id} value={farm.id}>
                      {farm.name}
                    </MenuItem>))
                  : <MenuItem disabled> No existen campos</MenuItem>} */}
            {/* </TextField> */}
          </Box>
          <Box className={classes.textFieldsContainer}>
            <TextField
              label='Sector (opcional)'
              variant='standard'
              fullWidth
              select
              className={classes.singleLeftTextfield}
              value={deviceInfo.sectorId}
              onChange={(e) => handleValueChange(e, 'sectorId')}
            >
              {/* CAMBIAR DESPOUES - REVISAR */}
              {(selectedFarm?.sectors && selectedFarm.sectors.length > 0)
                ? selectedFarm.sectors.map((sector) => (
                  <MenuItem key={sector.id} value={sector.id}>
                    {sector.name}
                  </MenuItem>))
                : <MenuItem disabled> No existen sectores</MenuItem>}
            </TextField>
            <TextField
              label='Subsector (opcional)'
              select
              variant='standard'
              fullWidth
              disabled={!deviceInfo.sectorId}
              value={deviceInfo.subSector}
              onChange={(e) => handleValueChange(e, 'subSector')}
            >
              {(selectedFarm?.sectors && selectedFarm.sectors.length > 0)
                ? selectedFarm.sectors
                  .find((sec) => sec.id === deviceInfo.sectorId)
                  ?.subSectors?.map((subSec) => (
                    <MenuItem key={subSec.subSectorId} value={subSec.subSectorId}>
                      {subSec.name}
                    </MenuItem>))
                : <MenuItem disabled> No existen subsectores</MenuItem>}
            </TextField>
          </Box>
          <Box className={classes.buttonContainer}>
            <Box className={classes.singleButtonContainer} marginRight='20px'>
              <CustomButton buttonText='cancelar' onClick={handleCloseAndResetData} />
            </Box>
            <Box className={classes.singleButtonContainer}>
              <CustomButton buttonText='siguiente' onClick={() => handleStep('secondStep')} />
            </Box>
          </Box>
        </Box>}
      {steps.secondStep && <AddDevicesFlowSecondStep
        deviceInfo={deviceInfo}
        setDeviceInfo={setDeviceInfo}
        isSensor={deviceInfo.deviceType === 'sensor'}
        handleCloseModal={handleCloseAndResetData}
        handleStep={handleStep}
        error={error}
        setError={setError}
        files={files}
        setFiles={setFiles}
      />}
      {steps.thirdStep && <AddDevicesFlowThirdStep
        title={deviceInfo.deviceType === 'sensor' ? 'Agregar sensor' : 'Agregar nodo'}
        detailedInfoTitle={deviceInfo.deviceType === 'sensor' ? 'Info sensor' : 'Info nodo'}
        detailedInfo={{
          Campo: deviceInfo.farm,
          Sector: (deviceInfo?.sectorId && deviceInfo.sectorId !== '')
            ? selectedFarm?.sectors?.find((sector) => sector.id === deviceInfo.sectorId)
              ?.wiseconnId ?? '-'
            : '-',
          Subsector: (deviceInfo?.subSector && deviceInfo.subSector !== '') ? deviceInfo.subSector : '-',
        }}
        handleCloseModal={handleCloseAndResetData}
        handleStep={handleStep}
        insertFunction={insertFunction}
        setCoordinates={setCoordinates}
      />}
    </>
  </CustomModal>
  );
};

export default AddDevicesFlow;
